import { ABOUT } from '@versports/ui'

/**
 * Documentos legales públicos, linkeados desde el modal "Acerca de"
 * (`AboutButton` de `@versports/ui`, rutas `ABOUT.termsPath` /
 * `ABOUT.dataPolicyPath`). Se montan bajo `AppShell` sin `RequireAuth`:
 * tienen que poder abrirse desde el login, desde un link de invitación o
 * desde el visor sin sesión.
 *
 * **Versión 1.1 (2026-09-28).** La 1.0 (2026-09-09) describía el kit con
 * base RPi y sensor electrodérmico; la 1.1 describe el único equipo de club
 * vigente (visor Meta Quest con la app VerSports Sensores, banda Polar H10 y
 * tablet del club; decisión de mg del 2026-09-28, `vrs-docs/58`) y saca de
 * la Política los datos que ese equipo no registra: ECG, acelerometría,
 * conductancia de la piel y batería (`vrs-docs/59`: sin base, la HRV sale del
 * RR de la notificación de pulso). Cambio redactado con IA, pendiente de
 * revisión de la asesoría legal del cliente.
 *
 * Textos completos redactados contra lo que el
 * sistema hace de verdad — inventario de datos de `vrs-docs/35` §3 y §9,
 * mapa de exposición y retención de `vrs-docs/52` §2/§6/§7, infra y
 * transferencia internacional de `vrs-docs/44`, roles y reportes de
 * `vrs-docs/30` — y contra el consentimiento normativo del piloto
 * (tutito, "Consentimiento informado", `302ad5be`). Regla de redacción: no se
 * afirma nada que el sistema no pueda cumplir hoy; donde el control existe
 * como procedimiento y no como automatismo, el texto lo dice así.
 *
 * En revisión con asesoría legal (mg, 2026-09-09). Los únicos placeholders
 * son los que sólo puede completar el cliente: razón social, CUIT, domicilio
 * y jurisdicción. No agregar más: si algo no se sabe, se averigua o no se
 * escribe.
 *
 * El consentimiento informado del estudio (`ConsentNotice`,
 * `StaffTermsGate`) es un documento **distinto** y normativo: lo define el
 * whitepaper en tutito, no este archivo. Estas páginas lo enlazan, no lo
 * reemplazan.
 */

/** Versión de los textos legales. La muestran las dos páginas y la fija el test. */
export const LEGAL_VERSION = '1.1'
/** Fecha de la versión vigente (AR). */
export const LEGAL_DATE = '2026-09-28'
/** Contacto oficial para consultas legales y ejercicio de derechos. */
export const LEGAL_CONTACT_EMAIL = ABOUT.contactEmail

function LegalShell({
  title,
  intro,
  children,
}: {
  title: string
  intro: string
  children: React.ReactNode
}) {
  return (
    <article className="mx-auto max-w-2xl px-lg py-2xl">
      <span className="tag-ok">
        Versión {LEGAL_VERSION} · {LEGAL_DATE} · en revisión con asesoría legal
      </span>
      <h1 className="mt-sm">{title}</h1>
      <p className="mt-sm text-sm leading-relaxed text-text-secondary">{intro}</p>
      <div className="mt-lg flex flex-col gap-md text-sm leading-relaxed text-text-secondary">
        {children}
      </div>
      <p className="mt-xl text-xs text-text-muted">
        © {ABOUT.year} {ABOUT.company}. Consultas y ejercicio de derechos:{' '}
        <a href={`mailto:${ABOUT.contactEmail}`}>{ABOUT.contactEmail}</a>
      </p>
    </article>
  )
}

/** Encabezado de sección. Mismo peso visual en los dos documentos. */
function H({ children }: { children: React.ReactNode }) {
  return <h2 className="m-0 mt-md text-base text-text-primary">{children}</h2>
}

/** Párrafo de cuerpo. Existe para no repetir `className` treinta veces. */
function P({ children }: { children: React.ReactNode }) {
  return <p className="m-0">{children}</p>
}

/** Lista de cuerpo. */
function L({ children }: { children: React.ReactNode }) {
  return <ul className="m-0 flex list-disc flex-col gap-xs pl-lg">{children}</ul>
}

export function TermsPage() {
  return (
    <LegalShell
      title="Términos y condiciones de uso"
      intro="Estos términos regulan el uso de la plataforma VerSports: la aplicación de realidad virtual, el panel web, los cuestionarios y el equipo que se usa en el club (visor de realidad virtual, banda de frecuencia cardíaca y tablet). Al crear tu cuenta, al aceptar los términos en pantalla o al usar cualquiera de esas superficies, aceptás lo que sigue. Si no estás de acuerdo, no uses el servicio."
    >
      <H>1. Quién presta el servicio</H>
      <P>
        VerSports es un servicio prestado por [RAZÓN SOCIAL], CUIT [CUIT], con
        domicilio en [DOMICILIO] (en adelante, VerSports o nosotros). VerSports™
        es marca de {ABOUT.company}. El desarrollo y la operación técnica de la
        plataforma están a cargo de Instituto Humai, que actúa por cuenta y
        orden de VerSports.
      </P>
      <P>
        Podés escribirnos por cualquier motivo relacionado con estos términos a{' '}
        <a href={`mailto:${ABOUT.contactEmail}`}>{ABOUT.contactEmail}</a>.
      </P>

      <H>2. Qué es VerSports y qué no es</H>
      <P>
        VerSports es una herramienta de entrenamiento de habilidades mentales y
        de rendimiento bajo presión. Combina una experiencia de realidad
        virtual, la medición de señales fisiológicas durante esa experiencia y
        cuestionarios psicométricos estandarizados, y devuelve informes de
        seguimiento al club y a los profesionales que trabajan con vos.
      </P>
      <P>
        VerSports no reemplaza tratamiento psicológico, diagnóstico médico ni
        evaluación clínica. No es terapia, no es diagnóstico y no es un estudio
        médico. Ningún resultado, índice ni informe de la plataforma constituye
        una indicación médica ni psicológica. Si tenés una condición de salud o
        una consulta clínica, hablá con un profesional de la salud.
      </P>
      <P>
        Los datos que la plataforma registra tampoco son una evaluación
        deportiva del club sobre vos ni una herramienta de selección. Cómo el
        club usa la información que recibe es responsabilidad del club, y está
        acotado por el consentimiento que vos otorgaste.
      </P>

      <H>3. Quién puede usar la plataforma</H>
      <P>
        El servicio se ofrece a clubes e instituciones deportivas en el marco
        del acuerdo comercial vigente con cada uno, y a las personas que ese
        club da de alta: jugadores y jugadoras, cuerpo técnico, coordinación y
        profesionales de la psicología del deporte. No hay registro abierto al
        público general.
      </P>
      <P>
        Si sos menor de 18 años, para usar la plataforma hace falta la
        autorización de tu madre, padre o representante legal, además de tu
        propia conformidad. Podés dejar de usarla en cualquier momento aunque
        esa autorización siga vigente.
      </P>

      <H>4. Cuentas y credenciales</H>
      <P>
        Las cuentas son personales e intransferibles. Sos responsable de la
        actividad que ocurra bajo tu cuenta y de no compartir tus credenciales
        (contraseña, PIN de jugador o link de invitación) con terceros. Si
        creés que alguien accedió a tu cuenta, avisanos a{' '}
        <a href={`mailto:${ABOUT.contactEmail}`}>{ABOUT.contactEmail}</a>.
      </P>
      <P>
        El staff del club es responsable de las invitaciones que emite, de la
        exactitud de los datos que carga sobre sus jugadores y de dar de baja a
        quien deja de pertenecer a la institución. El club debe contar con la
        autorización de la persona —o de su representante legal, si es menor—
        antes de cargar sus datos en la plataforma.
      </P>
      <P>
        Personal autorizado de VerSports puede acceder a una cuenta para dar
        soporte o resolver un problema, viendo la aplicación como la ve la
        persona usuaria. Cada uno de esos accesos queda registrado con quién lo
        hizo y cuándo.
      </P>

      <H>5. Uso del equipo: visor, banda y tablet</H>
      <P>
        El equipo está compuesto por un visor de realidad virtual Meta Quest,
        una banda torácica de frecuencia cardíaca Polar H10 y una tablet del
        club. La banda se conecta al visor por Bluetooth a través de la app
        VerSports Sensores, instalada en el visor. Salvo que el acuerdo con tu
        club diga otra cosa, el equipo se entrega en préstamo de uso: es
        propiedad de VerSports o del club, según ese acuerdo, y no puede
        venderse, cederse, modificarse ni abrirse.
      </P>
      <P>
        El visor es un equipo Meta Quest. La cuenta de Meta asociada al visor
        pertenece al club o a quien administre el dispositivo, no a VerSports:
        el uso del visor como dispositivo se rige además por los términos y la
        política de privacidad de Meta, y nosotros no recibimos los datos de esa
        cuenta.
      </P>
      <P>
        La banda es de contacto con la piel y de uso compartido dentro del
        club: se limpia entre usos según las indicaciones del fabricante. No
        está destinada a diagnóstico ni a monitoreo clínico, y no debe usarse
        como dispositivo médico.
      </P>

      <H>6. Salud y seguridad en realidad virtual</H>
      <P>
        Usar un visor de realidad virtual puede provocar mareo, náuseas,
        desorientación, fatiga visual o dolor de cabeza, sobre todo las primeras
        veces. Si sentís cualquiera de esos síntomas, sacate el visor y descansá
        antes de continuar. Podés interrumpir la experiencia en cualquier
        momento, sin dar explicaciones.
      </P>
      <P>
        Un porcentaje muy pequeño de personas puede sufrir convulsiones o
        pérdida de conciencia ante estímulos luminosos o patrones parpadeantes,
        aun sin antecedentes. Si tenés epilepsia fotosensible, antecedentes de
        convulsiones, alteraciones del equilibrio, un marcapasos u otro
        dispositivo implantado, o estás cursando un embarazo, consultá con un
        profesional de la salud antes de usar la plataforma y avisale al staff
        del club.
      </P>
      <P>
        Durante la sesión no ves la sala real. Jugá siempre en un espacio
        despejado, de pie y quieto en el lugar indicado, y siempre con una
        persona del equipo presente. VerSports no se responsabiliza por golpes,
        caídas o daños derivados de usar el visor en un entorno no despejado o
        sin acompañamiento.
      </P>
      <P>
        Las sesiones incluyen estímulos de presión —público, cuenta regresiva,
        flashes, sonido ambiente— diseñados a propósito para generar activación.
        El sonido no supera los 85 dB de pico y los estímulos luminosos se
        mantienen fuera del rango de frecuencias asociado a fotosensibilidad. Es
        esperable sentir nervios o frustración, parecidos a los de un partido.
        Si te resulta excesivo, frená.
      </P>

      <H>7. Participación en el estudio piloto</H>
      <P>
        Además del uso de la plataforma, VerSports lleva adelante un estudio
        piloto de investigación sobre entrenamiento mental. Participar del
        estudio es voluntario, se decide por separado y requiere un
        consentimiento informado específico —y, para menores de 18 años, la
        autorización de madre, padre o representante legal más el asentimiento
        de la propia persona—. Podés usar la plataforma sin participar del
        estudio, y podés retirarte del estudio cuando quieras sin que eso afecte
        tu lugar en el club ni tu acceso al servicio.
      </P>

      <H>8. Uso aceptable</H>
      <P>No podés, ni permitir que un tercero:</P>
      <L>
        <li>
          acceder a cuentas, informes o datos de otras personas sin
          autorización, ni intentar eludir los controles de acceso;
        </li>
        <li>
          usar la plataforma para evaluar, seleccionar o sancionar a una persona
          en contra de lo que ella consintió;
        </li>
        <li>
          alterar, descompilar o intervenir el software, incluida la app
          VerSports Sensores, el firmware del equipo o los datos que produce,
          ni falsear registros de sesión;
        </li>
        <li>
          extraer, copiar o redistribuir de forma masiva contenidos, informes o
          datos de la plataforma;
        </li>
        <li>
          usar el servicio para un fin ilícito o contrario a estos términos.
        </li>
      </L>

      <H>9. Propiedad intelectual</H>
      <P>
        El software, la experiencia de realidad virtual, los modelos y assets
        3D, el diseño de la plataforma, los índices y métricas derivadas, los
        informes y la marca VerSports pertenecen a VerSports o a sus licenciantes
        y están protegidos por la normativa de propiedad intelectual. El acceso
        al servicio no transfiere ninguno de esos derechos: te damos una
        licencia de uso personal, limitada, revocable y no transferible,
        mientras dure tu vínculo con el club contratante.
      </P>
      <P>
        Los cuestionarios psicométricos que se administran son instrumentos de
        terceros y se usan según las condiciones de sus autores. Tus respuestas y
        tus datos personales siguen siendo tuyos: el tratamiento que hacemos de
        ellos se describe en la <a href={ABOUT.dataPolicyPath}>Política de datos</a>.
      </P>

      <H>10. Disponibilidad, cambios y suspensión</H>
      <P>
        El servicio se presta tal como está disponible. Podemos modificar,
        suspender o discontinuar funcionalidades, y realizar tareas de
        mantenimiento que interrumpan temporalmente el acceso. Vamos a avisar
        con antelación razonable los cambios relevantes y las interrupciones
        programadas.
      </P>
      <P>
        Podemos suspender o dar de baja una cuenta que incumpla estos términos,
        que ponga en riesgo la seguridad de la plataforma o los datos de otras
        personas, o cuando termine el acuerdo con el club al que esa cuenta
        pertenece. Si la baja es por fin del acuerdo, se aplican los plazos de
        conservación de la Política de datos.
      </P>

      <H>11. Responsabilidad</H>
      <P>
        VerSports responde por el funcionamiento del servicio conforme a estos
        términos y a la normativa aplicable. No respondemos por el uso que el
        club o terceros hagan de los informes, por decisiones deportivas,
        laborales o de cualquier otra índole tomadas a partir de ellos, por
        fallas del equipamiento de terceros (visor, conectividad, dispositivos
        personales) ni por daños derivados de usar la plataforma fuera de las
        indicaciones de seguridad de la sección 6.
      </P>
      <P>
        Nada de lo anterior limita la responsabilidad que la ley no permite
        limitar, ni la que corresponde por dolo o culpa grave.
      </P>

      <H>12. Protección de datos personales</H>
      <P>
        El tratamiento de datos personales —incluidos datos de salud, que la Ley
        25.326 considera datos sensibles— se describe en detalle en la{' '}
        <a href={ABOUT.dataPolicyPath}>Política de datos</a>, que forma parte de
        estos términos. Ahí están las finalidades, quién accede, cuánto tiempo
        conservamos cada dato y cómo ejercer tus derechos.
      </P>

      <H>13. Cambios en estos términos</H>
      <P>
        Podemos actualizar estos términos. Cuando el cambio sea sustancial, lo
        vamos a avisar por correo electrónico o dentro de la aplicación antes de
        que entre en vigencia, y la versión y la fecha del encabezado se
        actualizan. Si el cambio afecta el tratamiento de tus datos personales
        de un modo que requiera tu consentimiento, te lo vamos a pedir.
      </P>

      <H>14. Ley aplicable y jurisdicción</H>
      <P>
        Estos términos se rigen por las leyes de la República Argentina. Para
        cualquier controversia derivada de ellos, las partes se someten a los
        tribunales ordinarios de [JURISDICCIÓN], con renuncia a cualquier otro
        fuero o jurisdicción que pudiera corresponder. Si sos consumidor, esta
        cláusula no te priva de acudir a los tribunales de tu domicilio ni de
        las vías administrativas de defensa del consumidor.
      </P>

      <H>15. Contacto</H>
      <P>
        Escribinos a{' '}
        <a href={`mailto:${ABOUT.contactEmail}`}>{ABOUT.contactEmail}</a> por
        consultas sobre estos términos, sobre tu cuenta o sobre el equipo. Para
        ejercer tus derechos sobre tus datos personales, mirá la{' '}
        <a href={ABOUT.dataPolicyPath}>Política de datos</a>.
      </P>
    </LegalShell>
  )
}

export function DataPolicyPage() {
  return (
    <LegalShell
      title="Política de datos personales"
      intro="Esta política explica qué datos personales tratamos, con qué finalidad, con qué base legal, quién accede a ellos, cuánto tiempo los conservamos y cómo podés ejercer tus derechos. Está redactada según la Ley 25.326 de Protección de los Datos Personales de la República Argentina y su reglamentación."
    >
      <H>1. Quién es el responsable del tratamiento</H>
      <P>
        El responsable de la base de datos es [RAZÓN SOCIAL], CUIT [CUIT], con
        domicilio en [DOMICILIO], que opera comercialmente como VerSports.
      </P>
      <P>
        Instituto Humai desarrolla y opera la plataforma por cuenta y orden de
        VerSports, en carácter de encargado del tratamiento: accede a los datos
        sólo para prestar ese servicio y bajo las instrucciones del responsable.
      </P>
      <P>
        Para cualquier tema vinculado a tus datos personales, escribinos a{' '}
        <a href={`mailto:${ABOUT.contactEmail}`}>{ABOUT.contactEmail}</a>.
      </P>

      <H>2. A quién se aplica</H>
      <P>
        A las personas que usan la plataforma: jugadores y jugadoras de los
        clubes que contratan el servicio, staff del club (coordinación, cuerpo
        técnico, dirección) y profesionales de la psicología del deporte que
        trabajan con esos datos. También cubre a quien participa del estudio
        piloto, con las precisiones de la sección 11.
      </P>

      <H>3. Qué datos tratamos</H>
      <P>
        Esta es la lista completa de lo que la plataforma efectivamente
        registra. Si en el futuro se activa una fuente nueva, esta política y su
        versión se actualizan antes.
      </P>
      <P>
        <strong>Datos de identificación y de cuenta.</strong> Nombre y apellido,
        correo electrónico, nombre para mostrar, club y categoría, edad o fecha
        de nacimiento, género, mano hábil, credenciales de acceso (contraseña o
        PIN, siempre almacenadas cifradas) y el resultado del chequeo de
        condiciones de participación.
      </P>
      <P>
        <strong>Datos de salud (datos sensibles).</strong> Frecuencia cardíaca
        e intervalos entre latidos, medidos con la banda torácica; y las
        respuestas al chequeo
        previo a la sesión, que es opcional y puede incluir horas de sueño,
        consumo de cafeína, último entrenamiento y, si corresponde, fase del
        ciclo menstrual. La Ley 25.326 clasifica los datos referentes a la salud
        como datos sensibles y los trata con protección reforzada: los tratamos
        con tu consentimiento expreso, escrito e informado.
      </P>
      <P>
        <strong>Telemetría de la sesión de realidad virtual.</strong> Posición y
        orientación de la cabeza y del control dentro del entorno virtual —es la
        trayectoria de tu movimiento en el juego, no una imagen tuya—, botones
        presionados y su duración, resultado de cada jugada, tiempos de
        decisión, dificultad aplicada, configuración de los estímulos de presión
        e interrupciones de la sesión con su motivo.
      </P>
      <P>
        <strong>Respuestas a cuestionarios psicométricos.</strong> Tus
        respuestas y los puntajes derivados de instrumentos estandarizados de
        fortaleza mental y de ansiedad precompetitiva, administrados en distintos
        momentos del seguimiento.
      </P>
      <P>
        <strong>Información derivada.</strong> Índices de regulación fisiológica
        y de rendimiento calculados a partir de lo anterior, series de
        seguimiento en el tiempo, informes en PDF por perfil de lectura y
        posiciones en el ranking del club.
      </P>
      <P>
        <strong>Datos técnicos.</strong> Registros de acceso y de eventos de
        autenticación, dirección IP y agente de usuario, el identificador de la
        banda de frecuencia cardíaca y la versión de la app del visor que
        transmitió la medición, y los datos necesarios para enviar y auditar el
        correo transaccional del producto.
      </P>
      <P>
        <strong>Qué no tratamos.</strong> No usamos cámara ni micrófono: la
        aplicación no accede a las cámaras del visor y no graba imagen, video ni
        audio de la sala. No hay seguimiento ocular. No tomamos muestras
        biológicas, no accedemos a tu historia clínica, no accedemos a tu
        teléfono, a tu ubicación ni a tus redes sociales, y no vendemos ni
        cedemos datos con fines publicitarios.
      </P>

      <H>4. Para qué los usamos</H>
      <L>
        <li>
          Darte acceso a la plataforma, gestionar tu cuenta y comunicarnos con
          vos por correo sobre tu cuenta, tus turnos y tus cuestionarios.
        </li>
        <li>
          Ejecutar y personalizar la sesión de entrenamiento, incluido el
          biofeedback que ves durante la experiencia.
        </li>
        <li>
          Calcular índices de regulación y rendimiento, y producir el
          seguimiento longitudinal de tu evolución.
        </li>
        <li>
          Generar los informes que recibe el club y los profesionales que
          trabajan con vos, en los términos del consentimiento que otorgaste.
        </li>
        <li>Mostrar el ranking entre jugadores, con el alcance de la sección 7.</li>
        <li>
          Investigar la eficacia del entrenamiento en el marco del estudio
          piloto, con las condiciones de la sección 11.
        </li>
        <li>
          Operar, mantener y asegurar la plataforma, prevenir accesos indebidos
          y cumplir obligaciones legales.
        </li>
      </L>

      <H>5. Con qué base legal</H>
      <P>
        La base principal es tu consentimiento libre, expreso e informado
        (artículos 5 y 6 de la Ley 25.326). Para los datos de salud, que son
        datos sensibles, el consentimiento es además escrito y específico
        (artículo 7). Consentir es voluntario: podés no hacerlo y no usar la
        plataforma, o consentir el uso deportivo y no el de investigación.
      </P>
      <P>
        También tratamos datos cuando es necesario para ejecutar la relación con
        el club contratante y para cumplir obligaciones legales que nos alcanzan.
      </P>
      <P>
        Podés retirar tu consentimiento en cualquier momento, escribiendo a{' '}
        <a href={`mailto:${ABOUT.contactEmail}`}>{ABOUT.contactEmail}</a>. El
        retiro no afecta la licitud del tratamiento hecho antes, y a partir de
        ese momento dejamos de registrar datos nuevos sobre vos.
      </P>

      <H>6. Menores de edad</H>
      <P>
        Parte del padrón son personas menores de 18 años. En esos casos el
        consentimiento lo otorga la madre, el padre o el representante legal, por
        escrito, y además le pedimos su conformidad a la propia persona menor,
        en un texto escrito para que lo entienda. Sin las dos cosas no se
        registran datos ni se realizan sesiones de estudio.
      </P>
      <P>
        Quien ejerce la representación legal puede retirar la autorización en
        cualquier momento y ejercer los derechos de la sección 12 en nombre de
        la persona menor. La persona menor puede dejar de participar por su
        cuenta, aunque la autorización siga vigente, y eso no tiene ninguna
        consecuencia dentro del club.
      </P>

      <H>7. Quién accede a tus datos y quién recibe informes</H>
      <L>
        <li>
          <strong>Staff de tu club</strong> (coordinación, cuerpo técnico,
          dirección): tus resultados de sesión y los informes de seguimiento,
          identificados con tu nombre, para el seguimiento deportivo. El staff
          sólo ve a los jugadores de su propio club.
        </li>
        <li>
          <strong>Profesional de psicología del deporte del proyecto</strong>:
          tus datos identificados, incluidos los puntajes psicométricos, para
          hacerte una devolución personal.
        </li>
        <li>
          <strong>Equipo de investigación</strong>: los datos del estudio
          asociados a un código, sin tu nombre ni tu correo.
        </li>
        <li>
          <strong>Equipo técnico de la plataforma</strong>: acceso a los
          sistemas donde viven los datos, sólo para mantener el servicio en
          funcionamiento.
        </li>
      </L>
      <P>
        Los puntajes de los cuestionarios no se te muestran dentro de la
        aplicación: es una decisión del profesional de psicología del deporte,
        porque esos puntajes se conversan en una devolución y no se leen solos en
        una pantalla. Podés pedirlos igual en cualquier momento ejerciendo tu
        derecho de acceso.
      </P>
      <P>
        <strong>Ranking.</strong> El ranking sólo se muestra a personas con
        sesión iniciada y está acotado al club de quien lo consulta: un jugador
        ve únicamente a jugadores de su mismo club. En el ranking aparece tu
        nombre para mostrar —nunca tu correo electrónico— y, si no configuraste
        uno, un alias derivado de tu código de participante. Lo que se publica
        ahí es tu posición y tu puntaje de juego, no tus datos fisiológicos ni
        tus puntajes psicométricos.
      </P>
      <P>
        No cedemos tus datos a terceros con fines comerciales ni publicitarios.
        Sólo los entregamos a una autoridad pública cuando una norma o una orden
        judicial nos obliga.
      </P>

      <H>8. Proveedores y transferencia internacional</H>
      <P>
        Para prestar el servicio trabajamos con proveedores que actúan como
        encargados del tratamiento, bajo contrato y sólo con las instrucciones
        que les damos:
      </P>
      <L>
        <li>
          <strong>Amazon Web Services</strong>: infraestructura de servidores,
          base de datos y almacenamiento de archivos. Los datos residen en la
          región <strong>América del Sur (San Pablo, Brasil)</strong>.
        </li>
        <li>
          <strong>Twilio SendGrid</strong>: envío del correo transaccional del
          producto (bienvenida, invitaciones, recordatorios), con servidores en
          los Estados Unidos.
        </li>
        <li>
          <strong>Cloudflare</strong>: resolución de nombres del dominio.
        </li>
        <li>
          <strong>Zoho</strong>: casilla de correo institucional donde recibimos
          tus consultas y tus pedidos de ejercicio de derechos.
        </li>
        <li>
          <strong>Instituto Humai</strong>: desarrollo y operación técnica de la
          plataforma, en la República Argentina.
        </li>
      </L>
      <P>
        Esto implica una <strong>transferencia internacional de datos</strong>:
        tus datos, incluidos los de salud, se almacenan y procesan fuera de la
        República Argentina. El artículo 12 de la Ley 25.326 admite la
        transferencia cuando el titular presta su consentimiento expreso, y
        además exigimos a cada proveedor garantías contractuales de
        confidencialidad y seguridad equivalentes a las que aplicamos nosotros.
        Al aceptar esta política prestás ese consentimiento; si no querés
        hacerlo, no vas a poder usar la plataforma, porque no tenemos hoy una
        alternativa de alojamiento local.
      </P>
      <P>
        El visor de realidad virtual es un equipo Meta Quest cuya cuenta
        pertenece al club o a quien administre el dispositivo. Meta trata los
        datos de esa cuenta bajo sus propias políticas: nosotros no las
        controlamos y no recibimos esos datos.
      </P>
      <P>
        Las tipografías de la aplicación se sirven desde Google Fonts, que por
        ese pedido conoce la dirección IP de tu navegador. No usamos servicios
        de analítica, de publicidad ni de seguimiento de terceros.
      </P>

      <H>9. Cuánto tiempo los conservamos</H>
      <P>
        Conservamos cada tipo de dato sólo mientras es necesario para las
        finalidades de la sección 4, según estos plazos:
      </P>
      <L>
        <li>
          <strong>Señal fisiológica cruda</strong>: identificada con tu código de
          participante durante todo el programa de investigación y hasta 10 años
          después de su cierre; después se anonimiza de forma irreversible y la
          versión anonimizada se conserva sin plazo para uso científico.
        </li>
        <li>
          <strong>Datos fisiológicos derivados y datos psicométricos</strong>:
          el mismo plazo que la señal cruda (programa de investigación más 10
          años); después se anonimizan de forma irreversible y se conservan sin
          plazo para uso científico.
        </li>
        <li>
          <strong>Datos de identificación y de cuenta</strong>: mientras la
          cuenta esté activa y hasta 1 año después de darla de baja.
        </li>
        <li>
          <strong>Registro de consentimiento y de participación</strong>: 10
          años, porque es la trazabilidad de que el tratamiento fue consentido.
        </li>
        <li>
          <strong>Datos técnicos</strong> (IP, agente de usuario, eventos de
          autenticación): 90 días. <strong>Registros de acceso del servidor</strong>:
          30 días.
        </li>
        <li>
          <strong>Copias de seguridad cifradas</strong>: 90 días, con rotación
          automática.
        </li>
        <li>
          <strong>Datos guardados en tu dispositivo</strong> (ver sección 10):
          hasta 30 días, y se borran al cerrar la sesión iniciada con el
          código de club.
        </li>
      </L>
      <P>
        Estos plazos se aplican por procedimiento operativo y se están
        implementando además como borrado automático. Un dato puede sobrevivir a
        su plazo dentro de una copia de seguridad ya escrita hasta que esa copia
        rota, porque las copias cifradas no se abren para editarlas.
      </P>

      <H>10. Cookies y datos guardados en tu dispositivo</H>
      <P>
        La sesión en el panel web se sostiene con una cookie propia, técnica y
        necesaria para mantenerte identificado: sin ella no podés usar el
        servicio. No usamos cookies de terceros, de publicidad ni de analítica.
      </P>
      <P>
        En el navegador o en el visor guardamos también, en el almacenamiento
        local del dispositivo, la vinculación del equipo con su club (el
        código de club) y una copia
        temporal de los datos de la sesión en curso, para que una caída de
        conexión no pierda la medición. Esa copia se sube y se borra; podés
        eliminarla vos mismo borrando los datos del sitio en el dispositivo.
      </P>

      <H>11. Uso de tus datos para investigación</H>
      <P>
        Si participás del estudio piloto, tus datos se usan además para
        investigar si el entrenamiento funciona. Para el análisis trabajamos con
        un <strong>código de participante</strong>, no con tu nombre: los
        archivos que se analizan salen con ese código.
      </P>
      <P>
        La correspondencia entre ese código y tu identidad se conserva hoy dentro
        de la misma base de producción, con acceso limitado al personal
        autorizado y con registro de cada descarga; estamos implementando su
        separación en una custodia diferenciada, y esta política se va a
        actualizar cuando eso esté en pie. Lo decimos así porque preferimos
        describir el control que existe y no el que quisiéramos tener.
      </P>
      <P>
        Con tu consentimiento, los datos del estudio (señal fisiológica,
        derivados y respuestas a cuestionarios) se conservan identificados con tu
        código durante todo el programa de investigación y hasta 10 años después
        de su cierre, para poder reanalizarlos, replicar resultados y responder a
        revisiones científicas. Cumplido ese plazo se anonimizan de forma
        irreversible (se destruye la correspondencia entre el código y tu
        identidad) y el conjunto anonimizado se conserva sin plazo con fines
        científicos. Podés retirar tu consentimiento antes de eso: en ese caso tus
        datos identificables se eliminan del estudio, sin afectar los análisis ya
        publicados de forma agregada.
      </P>
      <P>
        Los resultados se publican o se presentan siempre de forma agregada:
        nunca aparece tu nombre ni ningún dato que permita identificarte. Si te
        retirás del estudio, tus datos ya recolectados quedan excluidos de los
        análisis posteriores, y podés además pedir que los borremos según la
        sección 12. Lo que no se puede deshacer es un análisis ya publicado con
        datos agregados de todo el grupo, donde no aparecés identificado.
      </P>

      <H>12. Tus derechos y cómo ejercerlos</H>
      <P>
        Tenés derecho a <strong>acceder</strong> a tus datos, a{' '}
        <strong>rectificarlos</strong> si son inexactos o están desactualizados,
        a pedir su <strong>actualización</strong>, su{' '}
        <strong>supresión</strong> y su <strong>confidencialidad</strong>, y a{' '}
        <strong>oponerte</strong> a determinados tratamientos, en los términos de
        los artículos 14 a 16 de la Ley 25.326.
      </P>
      <P>
        Para ejercerlos, escribinos a{' '}
        <a href={`mailto:${ABOUT.contactEmail}`}>{ABOUT.contactEmail}</a>{' '}
        indicando qué querés hacer y acompañando una copia de tu documento de
        identidad, para asegurarnos de que sos vos. Si actuás en representación
        de una persona menor, indicá también el vínculo.
      </P>
      <P>
        Respondemos los pedidos de acceso dentro de los 10 días corridos de
        recibida la solicitud, y los de rectificación, actualización o supresión
        dentro de los 5 días hábiles, conforme a la ley.
      </P>
      <P>
        Sobre la supresión, dos precisiones honestas. Primero: el borrado alcanza
        a la base de datos y al almacenamiento de archivos crudos, se ejecuta de
        forma asistida por nuestro equipo y te confirmamos por escrito cuándo
        quedó hecho. Segundo: las copias de seguridad cifradas ya escritas no se
        abren para editarlas, así que un dato puede permanecer ahí hasta 90 días,
        hasta que esa copia rota. Además, conservamos el registro de que
        participaste y de que te retiraste, porque es parte de la trazabilidad
        del tratamiento.
      </P>
      <P>
        <em>
          El titular de los datos personales tiene la facultad de ejercer el
          derecho de acceso a los mismos en forma gratuita a intervalos no
          inferiores a seis meses, salvo que se acredite un interés legítimo al
          efecto conforme lo establecido en el artículo 14, inciso 3 de la Ley Nº
          25.326.
        </em>
      </P>
      <P>
        <em>
          La AGENCIA DE ACCESO A LA INFORMACIÓN PÚBLICA, en su carácter de Órgano
          de Control de la Ley Nº 25.326, tiene la atribución de atender las
          denuncias y reclamos que interpongan quienes resulten afectados en sus
          derechos por incumplimiento de las normas vigentes en materia de
          protección de datos personales.
        </em>
      </P>

      <H>13. Seguridad</H>
      <P>
        Aplicamos medidas técnicas y organizativas para proteger tus datos: las
        comunicaciones viajan cifradas con TLS, los discos de los servidores y
        las copias de seguridad están cifrados, el acceso está segmentado por rol
        y por club, las contraseñas y los PIN se guardan con funciones de
        derivación no reversibles, y los accesos de soporte a una cuenta quedan
        auditados.
      </P>
      <P>
        El visor conserva la copia temporal de la medición en su almacenamiento
        local hasta confirmar la subida; el equipo queda bajo resguardo físico
        en la sede del club y su acceso está restringido al personal autorizado
        y a quienes el club habilita para usarlo.
      </P>
      <P>
        Ningún sistema es infalible. Si ocurriera un incidente de seguridad que
        afecte tus datos, te lo vamos a comunicar y vamos a informar a la
        autoridad de control cuando corresponda.
      </P>

      <H>14. Cambios en esta política</H>
      <P>
        Si cambiamos qué datos tratamos, para qué o con quién los compartimos,
        actualizamos esta política, su número de versión y su fecha, y avisamos
        por correo o dentro de la aplicación. Cuando el cambio requiera un
        consentimiento nuevo, te lo vamos a pedir antes de aplicarlo.
      </P>

      <H>15. Documentos relacionados</H>
      <P>
        Estos textos se complementan con los{' '}
        <a href={ABOUT.termsPath}>Términos y condiciones de uso</a> y, para quien
        participa del estudio piloto, con el consentimiento informado específico
        que se firma por separado antes de la primera sesión. Ese consentimiento
        es el documento que define el alcance del uso científico de tus datos; si
        hubiera una diferencia entre aquel texto y esta política respecto del
        estudio, prevalece el consentimiento que firmaste.
      </P>

      <H>16. Contacto</H>
      <P>
        [RAZÓN SOCIAL] · CUIT [CUIT] · [DOMICILIO] ·{' '}
        <a href={`mailto:${ABOUT.contactEmail}`}>{ABOUT.contactEmail}</a>
      </P>
      <P>
        Versión {LEGAL_VERSION}, vigente desde el {LEGAL_DATE}.
      </P>
    </LegalShell>
  )
}
